import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { PageEvent } from '@angular/material/paginator';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';

import {
  SPS_PRUEFSTATUS_OPTIONEN,
  SpsAnlagenteilOptionen,
  SpsFortschritt,
  SpsPruefstatus,
  SpsPruefstatusOption,
  SpsSeite,
  SpsSignal,
  SpsSignalFilter,
  SpsSignalRichtung,
  SpsSignalSortierung,
  anlagenteilAuswahlLesen,
  anlagenteilAuswahlWert,
  leereSpsAnlagenteilOptionen,
  leererSpsFortschritt,
  leererSpsSignalFilter,
  listenTypInfo,
  passtZumStatusFilter,
  projektListenTypVonSps,
  pruefstatusOption,
  spsFilterAlsParams,
  spsFilterAusParams
} from '../../../../../Models/Sps';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AuthService } from '../../../../../api-authorization/authorize.service';
import { SpsPruefungApiService } from '../../../../../Services/sps-pruefung-api.service';
import { SpsDataService } from '../../../../../Services/offline/sps-data.service';
import { Datenquelle } from '../../../../../Models/offline.model';
import { SpsBemerkungDialogComponent, SpsBemerkungDialogData } from '../sps-bemerkung-dialog/sps-bemerkung-dialog.component';
import { SpsSignalLoeschenDialogComponent, SpsSignalLoeschenDialogData } from '../sps-signal-loeschen-dialog/sps-signal-loeschen-dialog.component';
import { SpsLoeschErgebnis } from '../../../../../Models/Sps';

/** Sortieroptionen der Tabelle (Backend-Werte). */
export const SPS_SORTIERUNGEN: { wert: SpsSignalSortierung; text: string }[] = [
  { wert: 'adresse', text: 'Adresse aufsteigend' },
  { wert: 'adresse_desc', text: 'Adresse absteigend' },
  { wert: 'name', text: 'Signalname A–Z' },
  { wert: 'name_desc', text: 'Signalname Z–A' },
  { wert: 'status', text: 'Prüfstatus' },
  { wert: 'station', text: 'Station' },
  { wert: 'geprueft_desc', text: 'Zuletzt geprüft' }
];

/**
 * Gemeinsame SPS-Prüfung eines Projekts: alle eindeutigen Signale, getrennt
 * nach Eingängen, Ausgängen und (falls vorhanden) unbekannter Richtung, mit
 * Suche, Statusfilter, Sortierung und serverseitiger Paginierung.
 *
 * Schnellprüfung: der Prüfstatus wird direkt in der Tabelle über ein Dropdown
 * gesetzt und sofort gespeichert – je Zeile unabhängig, mit Version
 * (HTTP 409 → Zeile neu laden), ohne die Tabelle neu aufzubauen. Die
 * bestehende Bemerkung wird dabei mitgesendet und bleibt erhalten; „Nicht OK“
 * fragt die Fehlerbeschreibung ab. Der Filterzustand steht in der URL, damit
 * Detailansicht und „Zurück“ denselben Stand wiederfinden.
 */
@Component({
  selector: 'app-sps-pruefung',
  templateUrl: './sps-pruefung.component.html',
  styleUrls: ['./sps-pruefung.component.css']
})
export class SpsPruefungComponent implements OnInit, OnDestroy {

  projektId = 0;

  /** Aktueller Filter – entspricht den Query-Parametern von GET api/sps/signale. */
  filter: SpsSignalFilter = leererSpsSignalFilter(0);

  seite: SpsSeite<SpsSignal> | null = null;
  fortschritt: SpsFortschritt = leererSpsFortschritt();

  /**
   * Optionen des Anlagenteil-Filters: nur die Anlagenteile, die bei den Signalen
   * DIESER Prüfung tatsächlich vorkommen (über alle Signale, nicht nur die Seite),
   * alphabetisch. Anlagenteile des Projekts ohne Signal fehlen absichtlich.
   */
  anlagenteilOptionen: SpsAnlagenteilOptionen = leereSpsAnlagenteilOptionen();

  /** Projekt, zu dem `anlagenteilOptionen` gehört, und Nummer der letzten Anfrage – ältere Antworten werden verworfen. */
  private anlagenteileProjektId = 0;
  private anlagenteilAnfrage = 0;

  readonly statusOptionen: SpsPruefstatusOption[] = SPS_PRUEFSTATUS_OPTIONEN;
  readonly sortierungen = SPS_SORTIERUNGEN;
  readonly seitenGroessen = [25, 50, 100, 250];

  erweitert = false;

  laedt = false;
  fehler = '';

  /** Signale, deren Statusänderung gerade läuft – nur diese Zeilen sind gesperrt. */
  private speichernd = new Set<number>();

  /** Kurz sichtbare Erfolgsmarkierung je Zeile. */
  private gespeichert = new Set<number>();

  /** Fehlermeldung je Zeile (z. B. Versionskonflikt). */
  zeilenFehler = new Map<number, string>();

  /** Nummer der zuletzt gestarteten Listenanfrage – ältere Antworten werden verworfen. */
  private anfrage = 0;

  private readonly textEingabe = new Subject<void>();
  private subs = new Subscription();

  /** Signale, deren Löschdialog gerade offen ist bzw. deren Löschen läuft. */
  private loeschend = new Set<number>();

  /**
   * Woher die Signale stammen. Offline wird aus dem lokalen Bestand des
   * heruntergeladenen Projekts gearbeitet; Prüfstatus lassen sich weiter
   * setzen, Löschen und Zuordnung klären brauchen den Server.
   */
  quelle: Datenquelle = 'server';
  standUtc: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: SpsPruefungApiService,
    private daten: SpsDataService,
    private dialog: MatDialog,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.subs.add(
      this.textEingabe.pipe(debounceTime(350)).subscribe(() => this.filterAnwenden()));

    this.subs.add(
      this.route.paramMap.subscribe(params => {
        this.projektId = Number(params.get('projektId')) || 0;
        this.filter = spsFilterAusParams(this.route.snapshot.queryParamMap, this.projektId);
        this.erweitert = !!(this.filter.name || this.filter.adresse || this.filter.bmk || this.filter.beschreibung);
        this.ladeAnlagenteile();
        this.ladeFortschritt();
        this.ladeSignale();
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get canEdit(): boolean {
    return this.authService.canEdit();
  }

  get istOffline(): boolean {
    return this.quelle === 'offline';
  }


  // =========================================================
  // LADEN
  // =========================================================

  ladeSignale(): void {
    if (!this.projektId) {
      this.fehler = 'Ungültige Projekt-Id.';
      return;
    }

    const nummer = ++this.anfrage;
    this.laedt = true;
    this.fehler = '';

    this.daten.getSignale(this.filter)
      .pipe(finalize(() => {
        if (nummer === this.anfrage) {
          this.laedt = false;
        }
      }))
      .subscribe({
        next: ergebnis => {
          if (nummer !== this.anfrage) {
            return;
          }

          const seite = ergebnis.daten;
          this.quelle = ergebnis.quelle;
          this.standUtc = ergebnis.standUtc ?? null;

          this.seite = seite;
          this.zeilenFehler.clear();

          // Seite hinter dem Ende (z. B. nach dem Filtern): auf die letzte Seite.
          const letzte = Math.max(1, Math.ceil(seite.gesamt / seite.groesse));
          if (seite.seite > letzte) {
            this.filter.seite = letzte;
            this.urlAktualisieren();
            this.ladeSignale();
          }
        },
        error: err => {
          if (nummer === this.anfrage) {
            this.fehler = err?.status === 404
              ? 'Das Projekt existiert nicht oder ist nicht zugänglich.'
              : SpsPruefungApiService.fehlertext(err, err?.message ?? 'Die Signale konnten nicht geladen werden.');
          }
        }
      });
  }

  ladeFortschritt(): void {
    if (!this.projektId) {
      return;
    }

    this.daten.getFortschritt(this.projektId).subscribe({
      next: f => this.fortschritt = f.daten,
      error: () => this.fortschritt = leererSpsFortschritt()
    });
  }

  /**
   * Filteroptionen „Anlagenteil“ – prüfungsbezogen: nur Anlagenteile, die bei den
   * Signalen dieses Projekts über die bestehende Signal-/Anlagenteilzuordnung
   * wirklich vorkommen, serverseitig über alle Signale ermittelt. Ein Fehler
   * lässt nur das Auswahlfeld leer; die Prüfung funktioniert weiter.
   */
  ladeAnlagenteile(): void {
    const projektId = this.projektId;
    const nummer = ++this.anlagenteilAnfrage;

    // Projektwechsel: nie die Optionen des vorherigen Projekts stehen lassen.
    if (projektId !== this.anlagenteileProjektId) {
      this.anlagenteilOptionen = leereSpsAnlagenteilOptionen();
      this.anlagenteileProjektId = projektId;
    }

    if (!projektId) {
      return;
    }

    this.daten.getPruefungAnlagenteile(projektId).subscribe({
      next: optionen => {
        // Eine verspätete Antwort eines früheren Projekts überschreibt nichts.
        if (nummer === this.anlagenteilAnfrage && projektId === this.projektId) {
          this.anlagenteilOptionen = optionen.daten;
        }
      },
      error: () => {
        if (nummer === this.anlagenteilAnfrage) {
          this.anlagenteilOptionen = leereSpsAnlagenteilOptionen();
        }
      }
    });
  }

  /** Alles neu laden – Kennzahlen, Anlagenteile und aktuelle Seite; Filter und Seite bleiben. */
  aktualisieren(): void {
    this.ladeAnlagenteile();
    this.ladeFortschritt();
    this.ladeSignale();
  }


  // =========================================================
  // FILTER (Eingänge | Ausgänge | Unbekannt, Status, Suche, Sortierung, Seite)
  // =========================================================

  get richtungen(): { wert: SpsSignalRichtung; text: string; icon: string; anzahl: number }[] {
    const r = [
      { wert: 'Eingang' as SpsSignalRichtung, text: 'Eingänge', icon: 'input', anzahl: this.fortschritt.eingaenge },
      { wert: 'Ausgang' as SpsSignalRichtung, text: 'Ausgänge', icon: 'output', anzahl: this.fortschritt.ausgaenge }
    ];

    if (this.fortschritt.richtungUnbekannt > 0 || this.filter.richtung === 'Unbekannt') {
      r.push({ wert: 'Unbekannt', text: 'Unbekannte Richtung', icon: 'help_outline', anzahl: this.fortschritt.richtungUnbekannt });
    }

    return r;
  }

  richtungSetzen(richtung: SpsSignalRichtung): void {
    if (this.filter.richtung === richtung) {
      return;
    }

    this.filter.richtung = richtung;
    this.filterAnwenden();
  }

  statusSetzen(status: SpsPruefstatus | null): void {
    this.filter.status = status;
    this.filterAnwenden();
  }

  nurOffeneSetzen(nurOffen: boolean): void {
    this.filter.nurOffen = nurOffen;
    this.filterAnwenden();
  }

  nurGeaendertSetzen(wert: boolean): void {
    this.filter.nurGeaendertSeitPruefung = wert;
    this.filterAnwenden();
  }

  /** Wert der Anlagenteil-Auswahl: null = alle, 'ohne' = ohne Anlagenteil, sonst die Id. */
  get anlagenteilAuswahl(): number | string | null {
    return anlagenteilAuswahlWert(this.filter.anlagenteilId, this.filter.ohneAnlagenteil);
  }

  anlagenteilSetzen(wert: number | string | null): void {
    const auswahl = anlagenteilAuswahlLesen(wert);
    this.filter.anlagenteilId = auswahl.anlagenteilId;
    this.filter.ohneAnlagenteil = auswahl.ohneAnlagenteil;
    this.filterAnwenden();
  }

  /**
   * Gefilterter Anlagenteil, der bei den Signalen dieser Prüfung (nicht mehr)
   * vorkommt – z. B. aus der URL eines anderen Stands. Er bleibt nur als aktive
   * Auswahl sichtbar; mit der nächsten Auswahl verschwindet er.
   */
  get anlagenteilAuswahlFehlt(): boolean {
    return this.filter.anlagenteilId != null
      && !this.anlagenteilOptionen.anlagenteile.some(a => a.anlagenteilId === this.filter.anlagenteilId);
  }

  /** „Ohne Anlagenteil“ nur anbieten, wenn es solche Signale gibt – oder der Filter gerade aktiv ist. */
  get zeigeOhneAnlagenteil(): boolean {
    return this.anlagenteilOptionen.ohneAnlagenteil > 0 || !!this.filter.ohneAnlagenteil;
  }

  /** Das Filterfeld entfällt, wenn kein Signal der Prüfung einen Anlagenteil kennt und nichts gefiltert wird. */
  get zeigeAnlagenteilFilter(): boolean {
    return this.anlagenteilOptionen.anlagenteile.length > 0
      || this.filter.anlagenteilId != null
      || !!this.filter.ohneAnlagenteil;
  }

  /** Tooltip des Anlagenteil-Chips: eindeutig oder – bei widersprüchlichen Quellen – alle Namen. */
  anlagenteilTooltip(signal: SpsSignal): string {
    if (signal.anlagenteilName) {
      return 'Anlagenteil (aus Ort bzw. Path der Liste)';
    }

    return 'Die Quellen dieses Signals nennen verschiedene Anlagenteile – keiner wird übernommen: '
      + (signal.anlagenteilNamen ?? []).join(', ');
  }

  sortierungSetzen(sortierung: SpsSignalSortierung): void {
    this.filter.sortierung = sortierung;
    this.filterAnwenden();
  }

  /** Texteingaben (Suche, Name, Adresse, BMK, Beschreibung) – mit kurzer Verzögerung. */
  textGeaendert(): void {
    this.textEingabe.next();
  }

  /** Enter in einem Suchfeld: sofort anwenden. */
  sucheAnwenden(): void {
    this.filterAnwenden();
  }

  sucheLeeren(): void {
    this.filter.suche = '';
    this.filterAnwenden();
  }

  erweitertUmschalten(): void {
    this.erweitert = !this.erweitert;

    if (!this.erweitert && (this.filter.name || this.filter.adresse || this.filter.bmk || this.filter.beschreibung)) {
      this.filter.name = '';
      this.filter.adresse = '';
      this.filter.bmk = '';
      this.filter.beschreibung = '';
      this.filterAnwenden();
    }
  }

  filterZuruecksetzen(): void {
    const richtung = this.filter.richtung;
    const groesse = this.filter.groesse;
    this.filter = { ...leererSpsSignalFilter(this.projektId), richtung, groesse };
    this.erweitert = false;
    this.filterAnwenden();
  }

  get filterAktiv(): boolean {
    const f = this.filter;
    return !!(f.status || f.suche || f.name || f.adresse || f.bmk || f.beschreibung || f.nurOffen || f.nurUngeklaert
      || f.nurGeaendertSeitPruefung || f.anlagenteilId != null || f.ohneAnlagenteil
      || (f.sortierung && f.sortierung !== 'adresse'));
  }

  private filterAnwenden(): void {
    this.filter.seite = 1;
    this.urlAktualisieren();
    this.ladeSignale();
  }

  seiteGewechselt(event: PageEvent): void {
    this.filter.seite = event.pageIndex + 1;

    if (event.pageSize !== this.filter.groesse) {
      this.filter.groesse = event.pageSize;
      this.filter.seite = 1;
    }

    this.urlAktualisieren();
    this.ladeSignale();
  }

  private urlAktualisieren(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: spsFilterAlsParams(this.filter),
      replaceUrl: true
    });
  }


  // =========================================================
  // SCHNELLPRÜFUNG (Status direkt in der Tabelle)
  // =========================================================

  wirdGespeichert(signal: SpsSignal): boolean {
    return this.speichernd.has(signal.id);
  }

  wurdeGespeichert(signal: SpsSignal): boolean {
    return this.gespeichert.has(signal.id);
  }

  /**
   * Dropdown-Auswahl einer Zeile. Speichert sofort – nur diese Zeile ist
   * währenddessen gesperrt; doppelte Anfragen für dasselbe Signal werden
   * verworfen. „Nicht OK“ verlangt eine Fehlerbeschreibung (Dialog).
   */
  statusAendern(signal: SpsSignal, neu: SpsPruefstatus, select?: MatSelect): void {
    if (!this.canEdit || neu === signal.pruefstatus) {
      return;
    }

    if (this.speichernd.has(signal.id)) {
      select?.writeValue(signal.pruefstatus);
      return;
    }

    if (neu === 'NichtOk') {
      const data: SpsBemerkungDialogData = {
        signalName: signal.name ?? '',
        adresse: signal.adresseNormiert,
        status: neu,
        bemerkung: signal.bemerkung ?? '',
        pflicht: true
      };

      this.dialog.open<SpsBemerkungDialogComponent, SpsBemerkungDialogData, string | null>(SpsBemerkungDialogComponent, {
        data, width: '480px', maxWidth: '95vw'
      }).afterClosed().subscribe(bemerkung => {
        if (bemerkung == null) {
          select?.writeValue(signal.pruefstatus);   // abgebrochen: Anzeige zurück
          return;
        }

        this.speichereStatus(signal, neu, bemerkung, select);
      });

      return;
    }

    // Andere Status: die vorhandene Bemerkung bleibt unverändert erhalten.
    this.speichereStatus(signal, neu, signal.bemerkung ?? '', select);
  }

  private speichereStatus(signal: SpsSignal, neu: SpsPruefstatus, bemerkung: string, select?: MatSelect): void {
    if (this.speichernd.has(signal.id)) {
      return;
    }

    this.speichernd.add(signal.id);
    this.zeilenFehler.delete(signal.id);
    this.gespeichert.delete(signal.id);

    this.daten.setStatus(signal, { status: neu, bemerkung: bemerkung || null, version: signal.version })
      .pipe(finalize(() => this.speichernd.delete(signal.id)))
      .subscribe({
        next: ergebnis => {
          const gespeichert = ergebnis.daten;

          this.zeileErsetzen(gespeichert);
          this.gespeichert.add(signal.id);
          setTimeout(() => this.gespeichert.delete(signal.id), 1800);

          if (ergebnis.lokal) {
            // Kein Fehler: die Prüfung liegt sicher im Gerät und geht bei
            // der nächsten Verbindung hinaus.
            this.snackBar.open('Lokal gespeichert – wird bei Verbindung synchronisiert.', 'OK', { duration: 3000 });
          }

          this.ladeFortschritt();

          // Passt das Signal nicht mehr zum aktiven Filter, verschwindet nur diese Zeile.
          if (!passtZumStatusFilter(this.filter, gespeichert.pruefstatus)) {
            this.zeileEntfernen(signal.id);
          }
        },
        error: err => {
          if (SpsPruefungApiService.istVersionskonflikt(err)) {
            // Jemand anderes war schneller: tatsächlichen Stand holen, Zeile aktualisieren, neu entscheiden lassen.
            this.zeilenFehler.set(signal.id,
              SpsPruefungApiService.fehlertext(err, 'Das Signal wurde zwischenzeitlich geändert.') + ' Die Zeile wurde aktualisiert – bitte erneut entscheiden.');
            this.zeileNeuLaden(signal.id, select);
            return;
          }

          select?.writeValue(signal.pruefstatus);
          this.zeilenFehler.set(signal.id, SpsPruefungApiService.fehlertext(err, err?.message ?? 'Der Prüfstatus konnte nicht gespeichert werden.'));
        }
      });
  }

  /** Eine Zeile durch den vom Backend gelieferten Stand ersetzen – ohne die Tabelle neu zu laden. */
  private zeileErsetzen(neu: SpsSignal): void {
    if (!this.seite) {
      return;
    }

    const i = this.seite.eintraege.findIndex(s => s.id === neu.id);

    if (i >= 0) {
      this.seite.eintraege[i] = { ...this.seite.eintraege[i], ...neu };
    }
  }

  private zeileEntfernen(signalId: number): void {
    if (!this.seite) {
      return;
    }

    const vorher = this.seite.eintraege.length;
    this.seite.eintraege = this.seite.eintraege.filter(s => s.id !== signalId);

    if (this.seite.eintraege.length < vorher) {
      this.seite.gesamt = Math.max(0, this.seite.gesamt - 1);
    }
  }

  private zeileNeuLaden(signalId: number, select?: MatSelect): void {
    this.daten.getSignal(signalId).subscribe({
      next: aktuell => {
        this.zeileErsetzen(aktuell.daten);
        select?.writeValue(aktuell.daten.pruefstatus);
        this.ladeFortschritt();
      },
      error: () => {
        // Signal nicht mehr vorhanden oder nicht erreichbar: Seite neu laden.
        this.ladeSignale();
      }
    });
  }

  zeilenFehlerSchliessen(signal: SpsSignal): void {
    this.zeilenFehler.delete(signal.id);
  }


  // =========================================================
  // SIGNAL LÖSCHEN (nur aus Prüfung / vollständig)
  // =========================================================

  wirdGeloescht(signal: SpsSignal): boolean {
    return this.loeschend.has(signal.id);
  }

  /**
   * Löschdialog mit beiden Wegen; der Dialog zeigt die betroffenen
   * Herkunftszeilen und führt die Löschung aus. Danach verschwindet nur
   * diese Zeile, die Kennzahlen werden neu geladen.
   */
  signalLoeschen(signal: SpsSignal, event?: Event): void {
    event?.stopPropagation();

    // Löschen braucht den Server – offline wird der Knopf gar nicht angeboten.
    if (!this.canEdit || this.istOffline || this.wirdGeloescht(signal) || this.wirdGespeichert(signal)) {
      return;
    }

    this.loeschend.add(signal.id);

    const data: SpsSignalLoeschenDialogData = {
      signalId: signal.id,
      name: signal.name,
      adresse: signal.adresseNormiert,
      pruefstatus: signal.pruefstatus
    };

    this.dialog.open<SpsSignalLoeschenDialogComponent, SpsSignalLoeschenDialogData, SpsLoeschErgebnis | null>(
      SpsSignalLoeschenDialogComponent, { data, width: '600px', maxWidth: '95vw', autoFocus: false }
    ).afterClosed().subscribe(ergebnis => {
      this.loeschend.delete(signal.id);

      if (!ergebnis) {
        return;
      }

      this.snackBar.open(SpsPruefungComponent.loeschErgebnisText(signal, ergebnis), 'OK', { duration: 6000 });
      this.zeileEntfernen(signal.id);
      this.fortschritt = ergebnis.fortschritt;

      // War es das letzte Signal mit diesem Anlagenteil, verschwindet er aus dem Filter.
      this.ladeAnlagenteile();
    });
  }

  static loeschErgebnisText(signal: SpsSignal, e: SpsLoeschErgebnis): string {
    const name = signal.name || signal.adresseNormiert || 'Signal';

    if (e.art === 'Vollstaendig') {
      return `„${name}“ vollständig gelöscht – samt ${e.eintraegeGeloescht} Herkunftszeile${e.eintraegeGeloescht === 1 ? '' : 'n'} und Prüfverlauf.`;
    }

    return `„${name}“ aus der Prüfung entfernt. ${e.eintraegeAusPruefungEntfernt} Herkunftszeile${e.eintraegeAusPruefungEntfernt === 1 ? ' bleibt' : 'n bleiben'} in den Listen erhalten`
      + (e.signalFolge === 'SignalArchiviert' ? '; Prüfstatus und Verlauf sind archiviert.' : '.');
  }


  // =========================================================
  // ANZEIGE
  // =========================================================

  option(status: SpsPruefstatus): SpsPruefstatusOption {
    return pruefstatusOption(status);
  }

  quellenText(signal: SpsSignal): string {
    const typen = signal.quellenTypen.map(t => listenTypInfo(projektListenTypVonSps(t)).titel);
    const basis = `${signal.quellenAnzahl} Quelle${signal.quellenAnzahl === 1 ? '' : 'n'}: ${typen.join(', ') || '–'}`;
    return signal.hatAbweichendeQuellen ? basis + ' – die Quellen widersprechen sich in einzelnen Angaben (siehe Signaldetails)' : basis;
  }

  get seitenAnzahl(): number {
    return this.seite ? Math.max(1, Math.ceil(this.seite.gesamt / this.seite.groesse)) : 1;
  }

  trackBySignal(_index: number, s: SpsSignal): number {
    return s.id;
  }


  // =========================================================
  // NAVIGATION
  // =========================================================

  oeffneSignal(signal: SpsSignal): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-signale', signal.id], {
      queryParams: { von: 'pruefung', ...spsFilterAlsParams(this.filter) }
    });
  }

  zurZuordnung(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-zuordnung']);
  }

  zuDenListen(): void {
    void this.router.navigate(['/projekte', this.projektId, 'sps-listen']);
  }

  zurueckZumProjekt(): void {
    void this.router.navigate(['/projekte', this.projektId]);
  }
}
